import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from "../config";
import {
  Package, Fuel, Users, Building2, AlertCircle,
  BarChart2, ChevronRight, Loader2, RefreshCw, TrendingUp,
  LayoutDashboard
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

// ─────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────
const MR_STATUS = {
  '-1': { label: 'Rejected', bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  '0':  { label: 'Draft',    bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
  '1':  { label: 'Pending',  bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
  '2':  { label: 'Approved', bg: '#dcfce7', color: '#16a34a', border: '#86efac' },
  '3':  { label: 'Issued',   bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
};

const DEPT_COLORS = {
  'Mechanical':    '#6366f1', 'Electrical':   '#f59e0b', 'Production':    '#10b981',
  'QC':            '#ec4899', 'Motorpool':    '#f97316', 'Logistics':     '#06b6d4',
  'Accounting':    '#8b5cf6', 'Spareparts':   '#14b8a6', 'RM Whse':       '#84cc16',
  'Packaging':     '#a78bfa', 'FG Whse':      '#fb923c', 'Admin/HR/MIS':  '#f43f5e',
  'Gen. Services': '#22d3ee', 'Fabrication':  '#4ade80', 'Branch':        '#fbbf24',
};

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────
const getDC       = (dept) => DEPT_COLORS[dept] || '#6366f1';
const getInit     = (name = '') => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
const isFuelApprv = (r) => !!(r.approvedBy && r.approvedBy.trim() !== '');
const fmtDate     = (d) => d ? new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtShort    = (d) => d ? new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }) : '';

// ─────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────
function AnimNum({ target, duration = 900 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let s = null;
    const step = (ts) => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target]);
  return <>{val}</>;
}

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div style={{ background: '#f1f5f9', borderRadius: 99, height: 6, overflow: 'hidden', flex: 1 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 1.1s ease' }} />
    </div>
  );
}

function Sparkline({ data, color }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 28 }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1, borderRadius: '2px 2px 0 0', minHeight: 3,
          height: `${(v / max) * 100}%`,
          background: i === data.length - 1 ? color : `${color}45`,
          transition: 'height .8s ease',
        }} />
      ))}
    </div>
  );
}

function SectionHead({ icon: Icon, label, accent, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8, background: `${accent}18`, color: accent, fontSize: 11, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase' }}>
        <Icon size={13} />{label}
      </div>
      <div style={{ height: 1, flex: 1, background: '#e2e8f0' }} />
      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{count} total</span>
    </div>
  );
}

function Pill({ children, bg, color, border }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: bg, color, border: `1px solid ${border}` }}>
      {children}
    </span>
  );
}

function Avatar({ name, dept, size = 34 }) {
  const color = getDC(dept);
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.35, flexShrink: 0, background: `${color}18`, color }}>
      {getInit(name)}
    </div>
  );
}

function ChartCard({ title, subtitle, children, style = {} }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden', ...style }}>
      <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: '16px 16px 8px' }}>{children}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: 'none', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,.2)' }}>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#fff', marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#94a3b8' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────
// CSS — no topnav styles
// ─────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash { font-family: 'Plus Jakarta Sans', sans-serif; background: #f8fafc; color: #1e293b; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
  .card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; transition: box-shadow .2s; }
  .card:hover { box-shadow: 0 4px 20px rgba(0,0,0,.07); }
  .kv { font-size: 36px; font-weight: 800; letter-spacing: -1.5px; line-height: 1; }
  .kl { font-size: 11px; font-weight: 700; letter-spacing: .7px; text-transform: uppercase; color: #94a3b8; }
  .tab-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
    cursor: pointer; border: 1.5px solid #e2e8f0; background: #fff; color: #64748b;
    transition: all .18s; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .tab-btn:hover:not(.tab-act) { border-color: #cbd5e1; background: #f8fafc; color: #334155; }
  .tab-btn.tab-act { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
  .tbl { width: 100%; border-collapse: collapse; }
  .tbl th { text-align: left; font-size: 10px; font-weight: 700; letter-spacing: .8px; color: #94a3b8; text-transform: uppercase; padding: 10px 14px; border-bottom: 2px solid #f1f5f9; }
  .tbl td { padding: 11px 14px; font-size: 12.5px; border-bottom: 1px solid #f8fafc; vertical-align: middle; }
  .tbl tr:last-child td { border-bottom: none; }
  .tbl tbody tr:hover td { background: #f8fafc; }
  .rc { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; cursor: pointer; transition: all .2s; }
  .rc:hover, .rc.sel { border-color: #3b82f6; background: #eff6ff; }
  .fade { opacity: 0; transform: translateY(10px); animation: up .45s forwards; }
  @keyframes up { to { opacity: 1; transform: none; } }
  .d1{animation-delay:.04s}.d2{animation-delay:.08s}.d3{animation-delay:.12s}.d4{animation-delay:.16s}
  .d5{animation-delay:.20s}.d6{animation-delay:.24s}.d7{animation-delay:.28s}.d8{animation-delay:.32s}
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────
export default function CombinedDashboard() {
  const [tab, setTab]         = useState('overview');
  const [mrs, setMrs]         = useState([]);
  const [fuels, setFuels]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selName, setSelName] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [mrRes, fuelRes] = await Promise.all([
        fetch(`${API_BASE_URL}api/MRHeader`),
        fetch(`${API_BASE_URL}api/FuelRequisition`),
      ]);
      if (mrRes.ok) {
        const mrRaw = await mrRes.json();
        const mrArr = Array.isArray(mrRaw) ? mrRaw : [];
        const withDetails = await Promise.all(mrArr.map(async (mr) => {
          try {
            const dr = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
            const det = dr.ok ? await dr.json() : [];
            return { ...mr, mrDetails: Array.isArray(det) ? det : [] };
          } catch { return { ...mr, mrDetails: [] }; }
        }));
        setMrs(withDetails);
      } else { setMrs([]); }

      if (fuelRes.ok) {
        const fuelRaw = await fuelRes.json();
        setFuels(Array.isArray(fuelRaw) ? fuelRaw : []);
      } else { setFuels([]); }
    } catch (e) {
      setError('Failed to connect to the API. Check your connection or API_BASE_URL.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── MR STATS ──
  const mrTotal    = mrs.length;
  const mrItems    = mrs.reduce((a, m) => a + (m.mrDetails?.length || 0), 0);
  const mrIssued   = mrs.filter(m => m.status === 3).length;
  const mrApproved = mrs.filter(m => m.status === 2).length;
  const mrPending  = mrs.filter(m => m.status === 1).length;
  const mrDraft    = mrs.filter(m => m.status === 0).length;
  const mrRejected = mrs.filter(m => m.status === -1).length;

  // ── FUEL STATS ──
  const fuelTotal      = fuels.length;
  const fuelApprCnt    = fuels.filter(isFuelApprv).length;
  const fuelPendCnt    = fuels.filter(f => !isFuelApprv(f)).length;
  const totalLitersReq = fuels.reduce((a, f) => a + (parseFloat(f.quantityLiters) || 0), 0);
  const totalLitersIss = fuels.filter(isFuelApprv).reduce((a, f) => a + (parseFloat(f.actualLiters) || 0), 0);

  const recentMR   = [...mrs].sort((a, b) => new Date(b.mrDate) - new Date(a.mrDate)).slice(0, 7);
  const recentFuel = [...fuels].sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate)).slice(0, 7);

  const combinedTrend = (() => {
    const days = 14;
    const today = new Date();
    return Array.from({ length: days }, (_, i) => {
      const d = new Date(today); d.setDate(d.getDate() - (days - 1) + i);
      const ds = d.toISOString().slice(0, 10);
      return {
        label: fmtShort(ds),
        MR: mrs.filter(m => (m.mrDate || '').slice(0, 10) === ds).length,
        Fuel: fuels.filter(f => (f.requestDate || '').slice(0, 10) === ds).length,
      };
    });
  })();

  const mrStatusPie = [
    { name: 'Issued',   value: mrIssued,   color: '#2563eb' },
    { name: 'Approved', value: mrApproved, color: '#7c3aed' },
    { name: 'Pending',  value: mrPending,  color: '#d97706' },
    { name: 'Draft',    value: mrDraft,    color: '#94a3b8' },
    { name: 'Rejected', value: mrRejected, color: '#dc2626' },
  ].filter(d => d.value > 0);

  const fuelStatusPie = [
    { name: 'Approved', value: fuelApprCnt,  color: '#16a34a' },
    { name: 'Pending',  value: fuelPendCnt,  color: '#d97706' },
  ].filter(d => d.value > 0);

  const deptBarData = (() => {
    const map = {};
    mrs.forEach(mr => {
      const d = mr.department || mr.departmentName || 'Unknown';
      if (!map[d]) map[d] = { dept: d, MRs: 0, Issued: 0 };
      map[d].MRs++;
      if (mr.status === 3) map[d].Issued++;
    });
    return Object.values(map).sort((a, b) => b.MRs - a.MRs).slice(0, 8);
  })();

  const fuelDeptBar = (() => {
    const map = {};
    fuels.forEach(f => {
      const d = f.department || 'Unknown';
      if (!map[d]) map[d] = { dept: d, Requested: 0, Issued: 0 };
      map[d].Requested += parseFloat(f.quantityLiters) || 0;
      if (isFuelApprv(f)) map[d].Issued += parseFloat(f.actualLiters) || 0;
    });
    return Object.values(map).sort((a, b) => b.Requested - a.Requested).slice(0, 8)
      .map(d => ({ ...d, Requested: +d.Requested.toFixed(1), Issued: +d.Issued.toFixed(1) }));
  })();

  const reqMap = {};
  mrs.forEach(mr => {
    const n = mr.requestedByName || 'Unknown';
    if (!reqMap[n]) reqMap[n] = { name: n, mrTotal: 0, mrItems: 0, fuelTotal: 0, fuelLiters: 0, depts: new Set(), mrs: [], fuels: [] };
    reqMap[n].mrTotal++;
    reqMap[n].mrItems += mr.mrDetails?.length || 0;
    reqMap[n].depts.add(mr.department || mr.departmentName || '');
    reqMap[n].mrs.push(mr);
  });
  fuels.forEach(f => {
    const n = f.requestorName || 'Unknown';
    if (!reqMap[n]) reqMap[n] = { name: n, mrTotal: 0, mrItems: 0, fuelTotal: 0, fuelLiters: 0, depts: new Set(), mrs: [], fuels: [] };
    reqMap[n].fuelTotal++;
    reqMap[n].fuelLiters += parseFloat(f.quantityLiters) || 0;
    reqMap[n].depts.add(f.department || '');
    reqMap[n].fuels.push(f);
  });
  const requestors = Object.values(reqMap).sort((a, b) => (b.mrTotal + b.fuelTotal) - (a.mrTotal + a.fuelTotal));
  const selReq     = selName ? reqMap[selName] : null;
  const maxReqTot  = requestors.length > 0 ? (requestors[0].mrTotal + requestors[0].fuelTotal) : 1;

  const reqBarData = requestors.slice(0, 8).map(r => ({
    name: r.name.split(' ')[0],
    MRs: r.mrTotal,
    Fuel: r.fuelTotal,
  }));

  const deptMap = {};
  mrs.forEach(mr => {
    const d = mr.department || mr.departmentName || 'Unknown';
    if (!deptMap[d]) deptMap[d] = { dept: d, mrTotal: 0, mrIssued: 0, fuelTotal: 0, fuelLiters: 0 };
    deptMap[d].mrTotal++;
    if (mr.status === 3) deptMap[d].mrIssued++;
  });
  fuels.forEach(f => {
    const d = f.department || 'Unknown';
    if (!deptMap[d]) deptMap[d] = { dept: d, mrTotal: 0, mrIssued: 0, fuelTotal: 0, fuelLiters: 0 };
    deptMap[d].fuelTotal++;
    deptMap[d].fuelLiters += parseFloat(f.quantityLiters) || 0;
  });
  const depts     = Object.values(deptMap).sort((a, b) => b.mrTotal - a.mrTotal);
  const maxDeptMR = Math.max(...depts.map(d => d.mrTotal), 1);

  if (loading) return (
    <><style>{CSS}</style>
    <div className="dash" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <Loader2 size={40} color="#2563eb" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ fontWeight: 700, color: '#334155', fontSize: 15 }}>Loading dashboard...</div>
        <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Fetching MR & Fuel data from API</div>
      </div>
    </div></>
  );

  if (error) return (
    <><style>{CSS}</style>
    <div className="dash" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <AlertCircle size={40} color="#dc2626" style={{ margin: '0 auto 16px' }} />
        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15, marginBottom: 8 }}>{error}</div>
        <button onClick={fetchAll} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    </div></>
  );

  // ─────────────────────────────────────────
  // RENDER — no sticky topnav, tabs are inline
  // ─────────────────────────────────────────
  return (
    <><style>{CSS}</style>
    <div className="dash">
      <div style={{ padding: '24px 28px', maxWidth: 1440, margin: '0 auto' }}>

        {/* ── PAGE HEADER ROW (no nav, just title + tabs + refresh) ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          {/* Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)', borderRadius: 12, padding: '8px 10px', display: 'flex', gap: 5 }}>
              <Package size={16} color="#fff" /><Fuel size={16} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#1e293b', letterSpacing: '-.3px' }}>Operations Dashboard</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Spare Parts &amp; Fuel Requisition</div>
            </div>
          </div>

          {/* Tabs as card-style buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              ['overview',     'Overview',    <LayoutDashboard size={13} />],
              ['analytics',    'Analytics',   <TrendingUp size={13} />],
              ['requestors',   'Requestors',  <Users size={13} />],
              ['departments',  'Departments', <Building2 size={13} />],
            ].map(([k, l, icon]) => (
              <button key={k} className={`tab-btn ${tab === k ? 'tab-act' : ''}`} onClick={() => setTab(k)}>
                {icon}{l}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={fetchAll}
            style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* ═══════════════════════════════
            OVERVIEW TAB
        ═══════════════════════════════ */}
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* SPARE PARTS KPI ROW */}
            <div>
              <SectionHead icon={Package} label="Spare Parts Item Requests" accent="#2563eb" count={mrTotal} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
                <div className="card fade d1" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', border: 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div className="kl" style={{ color: 'rgba(255,255,255,.65)' }}>Total MRs</div>
                      <div className="kv" style={{ color: '#fff', marginTop: 4 }}><AnimNum target={mrTotal} /></div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,.65)', marginTop: 6 }}>
                        <span style={{ color: '#bfdbfe', fontWeight: 700 }}>{mrItems}</span> line items requested
                      </div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,.15)', borderRadius: 12, padding: 10 }}><Package size={22} color="#fff" /></div>
                  </div>
                  <div style={{ marginTop: 18 }}>
                    <Sparkline data={combinedTrend.map(d => d.MR)} color="rgba(255,255,255,.9)" />
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', marginTop: 3 }}>Last 14 days</div>
                  </div>
                </div>
                <div className="card fade d2">
                  <div className="kl">Issued</div>
                  <div className="kv" style={{ color: '#16a34a', marginTop: 4 }}><AnimNum target={mrIssued} /></div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: '#64748b' }}>Fulfillment</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>{mrTotal > 0 ? Math.round((mrIssued/mrTotal)*100) : 0}%</span>
                    </div>
                    <ProgressBar value={mrIssued} max={mrTotal} color="#16a34a" />
                  </div>
                </div>
                <div className="card fade d3">
                  <div className="kl">Approved</div>
                  <div className="kv" style={{ color: '#7c3aed', marginTop: 4 }}><AnimNum target={mrApproved} /></div>
                  <div style={{ marginTop: 12 }}>
                    <ProgressBar value={mrApproved} max={mrTotal} color="#7c3aed" />
                    <div style={{ marginTop: 8 }}><Pill bg="#fef3c7" color="#d97706" border="#fde68a">{mrPending} pending</Pill></div>
                  </div>
                </div>
                <div className="card fade d4">
                  <div className="kl">Needs Action</div>
                  <div className="kv" style={{ color: '#dc2626', marginTop: 4 }}><AnimNum target={mrPending + mrDraft} /></div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                    <Pill bg="#fef3c7" color="#d97706" border="#fde68a">{mrPending} Pending</Pill>
                    <Pill bg="#f1f5f9" color="#64748b" border="#e2e8f0">{mrDraft} Draft</Pill>
                    <Pill bg="#fee2e2" color="#dc2626" border="#fca5a5">{mrRejected} Rejected</Pill>
                  </div>
                </div>
              </div>
            </div>

            {/* FUEL KPI ROW */}
            <div>
              <SectionHead icon={Fuel} label="Fuel Requisitions" accent="#ea580c" count={fuelTotal} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
                <div className="card fade d1" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg,#ea580c,#c2410c)', border: 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div className="kl" style={{ color: 'rgba(255,255,255,.65)' }}>Total Fuel Requests</div>
                      <div className="kv" style={{ color: '#fff', marginTop: 4 }}><AnimNum target={fuelTotal} /></div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,.65)', marginTop: 6 }}>
                        <span style={{ color: '#fed7aa', fontWeight: 700 }}>{totalLitersReq.toFixed(0)}L</span> total requested
                      </div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,.15)', borderRadius: 12, padding: 10 }}><Fuel size={22} color="#fff" /></div>
                  </div>
                  <div style={{ marginTop: 18 }}>
                    <Sparkline data={combinedTrend.map(d => d.Fuel)} color="rgba(255,255,255,.9)" />
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', marginTop: 3 }}>Last 14 days</div>
                  </div>
                </div>
                <div className="card fade d2">
                  <div className="kl">Approved</div>
                  <div className="kv" style={{ color: '#16a34a', marginTop: 4 }}><AnimNum target={fuelApprCnt} /></div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: '#64748b' }}>Approval rate</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>{fuelTotal > 0 ? Math.round((fuelApprCnt/fuelTotal)*100) : 0}%</span>
                    </div>
                    <ProgressBar value={fuelApprCnt} max={fuelTotal} color="#16a34a" />
                  </div>
                </div>
                <div className="card fade d3">
                  <div className="kl">Pending Approval</div>
                  <div className="kv" style={{ color: '#d97706', marginTop: 4 }}><AnimNum target={fuelPendCnt} /></div>
                  <div style={{ marginTop: 12 }}>
                    <ProgressBar value={fuelPendCnt} max={fuelTotal} color="#d97706" />
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>awaiting review</div>
                  </div>
                </div>
                <div className="card fade d4">
                  <div className="kl">Liters Issued</div>
                  <div className="kv" style={{ color: '#0891b2', marginTop: 4, fontSize: 28 }}>
                    <AnimNum target={Math.round(totalLitersIss)} /><span style={{ fontSize: 14, fontWeight: 600, marginLeft: 2 }}>L</span>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: '#64748b' }}>of {totalLitersReq.toFixed(0)}L req.</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>{totalLitersReq > 0 ? Math.round((totalLitersIss/totalLitersReq)*100) : 0}%</span>
                    </div>
                    <ProgressBar value={totalLitersIss} max={totalLitersReq || 1} color="#0891b2" />
                  </div>
                </div>
              </div>
            </div>

            {/* RECENT TABLES */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div className="card fade d5" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ background: '#eff6ff', borderRadius: 8, padding: '5px 6px', display: 'flex' }}><Package size={14} color="#2563eb" /></div>
                  <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>Recent MRs</span>
                </div>
                {recentMR.length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No MRs found</div>
                  : <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                      <table className="tbl">
                        <thead><tr><th>Name</th><th>Dept</th><th>Items</th><th>Status</th><th>Date</th></tr></thead>
                        <tbody>
                          {recentMR.map(mr => {
                            const st = MR_STATUS[String(mr.status)] || MR_STATUS['0'];
                            const dept = mr.department || mr.departmentName || '';
                            return (
                              <tr key={mr.id}>
                                <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Avatar name={mr.requestedByName || ''} dept={dept} />
                                  <span style={{ fontWeight: 600, color: '#334155', fontSize: 12 }}>{(mr.requestedByName || '—').split(' ')[0]}</span>
                                </div></td>
                                <td><span style={{ fontSize: 11, color: getDC(dept), fontWeight: 700 }}>{dept || '—'}</span></td>
                                <td><span style={{ fontWeight: 700, color: '#2563eb' }}>{mr.mrDetails?.length || 0}</span></td>
                                <td><Pill bg={st.bg} color={st.color} border={st.border}>{st.label}</Pill></td>
                                <td><span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(mr.mrDate)}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
              <div className="card fade d6" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ background: '#fff7ed', borderRadius: 8, padding: '5px 6px', display: 'flex' }}><Fuel size={14} color="#ea580c" /></div>
                  <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>Recent Fuel Requests</span>
                </div>
                {recentFuel.length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No fuel requests found</div>
                  : <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                      <table className="tbl">
                        <thead><tr><th>Requestor</th><th>Plate</th><th>Qty</th><th>Status</th><th>Date</th></tr></thead>
                        <tbody>
                          {recentFuel.map(f => (
                            <tr key={f.id}>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Avatar name={f.requestorName || ''} dept={f.department || ''} />
                                <span style={{ fontWeight: 600, color: '#334155', fontSize: 12 }}>{(f.requestorName || '—').split(' ')[0]}</span>
                              </div></td>
                              <td><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: 11 }}>{f.plateNumber || '—'}</span></td>
                              <td><span style={{ fontWeight: 700, color: '#ea580c' }}>{f.quantityLiters ?? '—'}L</span></td>
                              <td>{isFuelApprv(f) ? <Pill bg="#dcfce7" color="#16a34a" border="#86efac">Approved</Pill> : <Pill bg="#fef3c7" color="#d97706" border="#fde68a">Pending</Pill>}</td>
                              <td><span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(f.requestDate)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════
            ANALYTICS TAB
        ═══════════════════════════════ */}
        {tab === 'analytics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ChartCard title="MR & Fuel Activity — Last 14 Days" subtitle="Daily request volume trend">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={combinedTrend} margin={{ top: 5, right: 16, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fuelGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ea580c" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#ea580c" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600, paddingTop: 8 }} />
                  <Area type="monotone" dataKey="MR" stroke="#2563eb" strokeWidth={2.5} fill="url(#mrGrad)" dot={false} activeDot={{ r: 5, fill: '#2563eb' }} />
                  <Area type="monotone" dataKey="Fuel" stroke="#ea580c" strokeWidth={2.5} fill="url(#fuelGrad)" dot={false} activeDot={{ r: 5, fill: '#ea580c' }} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <ChartCard title="MR Status Breakdown" subtitle="Distribution by current status">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <ResponsiveContainer width={160} height={160}>
                    <PieChart>
                      <Pie data={mrStatusPie} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value">
                        {mrStatusPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {mrStatusPie.map((item) => (
                      <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{item.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ProgressBar value={item.value} max={mrTotal} color={item.color} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', minWidth: 22, textAlign: 'right' }}>{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </ChartCard>

              <ChartCard title="Fuel Approval Breakdown" subtitle={`${totalLitersReq.toFixed(0)}L total requested`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <ResponsiveContainer width={160} height={160}>
                      <PieChart>
                        <Pie data={fuelStatusPie} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value">
                          {fuelStatusPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{fuelTotal}</div>
                      <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Total</div>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {fuelStatusPie.map((item) => (
                      <div key={item.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
                            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{item.name}</span>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 800, color: item.color }}>{item.value}</span>
                        </div>
                        <ProgressBar value={item.value} max={fuelTotal} color={item.color} />
                      </div>
                    ))}
                    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', marginTop: 4 }}>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Liters Efficiency</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#0891b2' }}>{totalLitersIss.toFixed(0)}L</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>issued of {totalLitersReq.toFixed(0)}L</span>
                      </div>
                      <ProgressBar value={totalLitersIss} max={totalLitersReq || 1} color="#0891b2" />
                    </div>
                  </div>
                </div>
              </ChartCard>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <ChartCard title="MR Volume by Department" subtitle="Total requested vs. issued">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={deptBarData} margin={{ top: 5, right: 8, left: -10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="dept" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} angle={-30} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, fontWeight: 600, paddingTop: 4 }} />
                    <Bar dataKey="MRs"    fill="#2563eb" radius={[4,4,0,0]} />
                    <Bar dataKey="Issued" fill="#10b981" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Fuel Liters by Department" subtitle="Requested vs. actually issued">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={fuelDeptBar} margin={{ top: 5, right: 8, left: -10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="dept" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} angle={-30} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, fontWeight: 600, paddingTop: 4 }} />
                    <Bar dataKey="Requested" fill="#ea580c" radius={[4,4,0,0]} />
                    <Bar dataKey="Issued"    fill="#0891b2" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard title="Top Requestors" subtitle="MR count vs. Fuel requests per person">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={reqBarData} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }} tickLine={false} axisLine={false} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, fontWeight: 600, paddingTop: 8 }} />
                  <Bar dataKey="MRs"  fill="#2563eb" radius={[0,4,4,0]} barSize={10} />
                  <Bar dataKey="Fuel" fill="#ea580c" radius={[0,4,4,0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* ═══════════════════════════════
            REQUESTORS TAB
        ═══════════════════════════════ */}
        {tab === 'requestors' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Users size={18} color="#2563eb" />
              <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>People Overview</span>
              <Pill bg="#eff6ff" color="#2563eb" border="#bfdbfe">{requestors.length} people</Pill>
            </div>
            {requestors.length === 0
              ? <div className="card" style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>No data yet</div>
              : <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto' }}>
                    {requestors.map((req, i) => {
                      const topDept = [...req.depts].filter(Boolean)[0] || '';
                      const color = getDC(topDept);
                      const total = req.mrTotal + req.fuelTotal;
                      return (
                        <div key={req.name} className={`rc fade d${Math.min(i+1,8)} ${selName===req.name?'sel':''}`} style={{ animationDelay: `${i*.05}s` }} onClick={() => setSelName(selName===req.name?null:req.name)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Avatar name={req.name} dept={topDept} size={38} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{req.name}</div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                                {req.mrTotal > 0 && <span style={{ fontSize: 11, color: '#2563eb' }}>{req.mrTotal} MRs</span>}
                                {req.fuelTotal > 0 && <span style={{ fontSize: 11, color: '#ea580c' }}>{req.fuelTotal} Fuel</span>}
                              </div>
                            </div>
                            <ChevronRight size={14} color={selName===req.name?'#2563eb':'#cbd5e1'} />
                          </div>
                          <div style={{ marginTop: 10 }}><ProgressBar value={total} max={maxReqTot} color={color} /></div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="card" style={{ minHeight: 400 }}>
                    {!selReq
                      ? <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, opacity: .35 }}>
                          <Users size={40} color="#94a3b8" />
                          <p style={{ fontSize: 14, color: '#94a3b8' }}>Select a person to see their requests</p>
                        </div>
                      : <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
                            <Avatar name={selReq.name} dept={[...selReq.depts].filter(Boolean)[0] || ''} size={52} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b' }}>{selReq.name}</div>
                              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                                {[...selReq.depts].filter(Boolean).map(d => <Pill key={d} bg={`${getDC(d)}18`} color={getDC(d)} border={`${getDC(d)}35`}>{d}</Pill>)}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 20 }}>
                              {[['MRs', selReq.mrTotal, '#2563eb'], ['Items', selReq.mrItems, '#7c3aed'], ['Fuel RQs', selReq.fuelTotal, '#ea580c'], ['Liters', `${selReq.fuelLiters.toFixed(0)}L`, '#0891b2']].map(([l,v,c]) => (
                                <div key={l} style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{v}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{l}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            {selReq.mrs.length > 0 && (
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                  <div style={{ background: '#eff6ff', borderRadius: 6, padding: '3px 5px' }}><Package size={12} color="#2563eb" /></div>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Material Requests ({selReq.mrs.length})</span>
                                </div>
                                <div style={{ borderRadius: 10, border: '1px solid #f1f5f9', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                                  <table className="tbl">
                                    <thead><tr><th>Control</th><th>Dept</th><th>Status</th></tr></thead>
                                    <tbody>
                                      {selReq.mrs.map(mr => {
                                        const st = MR_STATUS[String(mr.status)] || MR_STATUS['0'];
                                        const dept = mr.department || mr.departmentName || '—';
                                        return <tr key={mr.id}><td><span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2563eb', fontWeight: 700 }}>{(mr.controlNo||mr.mrNo||'').slice(-10)}</span></td><td><span style={{ fontSize: 11, color: getDC(dept), fontWeight: 600 }}>{dept}</span></td><td><Pill bg={st.bg} color={st.color} border={st.border}>{st.label}</Pill></td></tr>;
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {selReq.fuels.length > 0 && (
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                  <div style={{ background: '#fff7ed', borderRadius: 6, padding: '3px 5px' }}><Fuel size={12} color="#ea580c" /></div>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Fuel Requests ({selReq.fuels.length})</span>
                                </div>
                                <div style={{ borderRadius: 10, border: '1px solid #f1f5f9', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                                  <table className="tbl">
                                    <thead><tr><th>Control</th><th>Plate</th><th>Qty</th><th>Status</th></tr></thead>
                                    <tbody>
                                      {selReq.fuels.map(f => <tr key={f.id}><td><span style={{ fontFamily: 'monospace', fontSize: 10, color: '#ea580c', fontWeight: 700 }}>{(f.controlNo||'').slice(-10)}</span></td><td><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: 11 }}>{f.plateNumber||'—'}</span></td><td><span style={{ fontWeight: 700, color: '#ea580c' }}>{f.quantityLiters??'—'}L</span></td><td>{isFuelApprv(f)?<Pill bg="#dcfce7" color="#16a34a" border="#86efac">Approved</Pill>:<Pill bg="#fef3c7" color="#d97706" border="#fde68a">Pending</Pill>}</td></tr>)}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                    }
                  </div>
                </div>
            }
          </div>
        )}

        {/* ═══════════════════════════════
            DEPARTMENTS TAB
        ═══════════════════════════════ */}
        {tab === 'departments' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Building2 size={18} color="#2563eb" />
              <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>Department Analytics</span>
              <Pill bg="#eff6ff" color="#2563eb" border="#bfdbfe">{depts.length} departments</Pill>
            </div>

            {depts.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
                {depts.slice(0,3).map((d,i) => {
                  const color = getDC(d.dept);
                  const medals = ['🥇','🥈','🥉'];
                  const fRate = d.mrTotal > 0 ? Math.round((d.mrIssued/d.mrTotal)*100) : 0;
                  return (
                    <div key={d.dept} className={`card fade d${i+1}`} style={{ borderLeft: `4px solid ${color}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <span style={{ fontSize: 20 }}>{medals[i]}</span>
                        <Pill bg={`${color}18`} color={color} border={`${color}35`}>{d.dept}</Pill>
                      </div>
                      <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
                        <div><div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: '-1px' }}>{d.mrTotal}</div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>MRs</div></div>
                        <div style={{ width: 1, background: '#f1f5f9' }} />
                        <div><div style={{ fontSize: 28, fontWeight: 800, color: '#ea580c', letterSpacing: '-1px' }}>{d.fuelLiters.toFixed(0)}</div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Liters Req.</div></div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>MR Fulfillment</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color }}>{fRate}%</span>
                          </div>
                          <ProgressBar value={d.mrIssued} max={d.mrTotal||1} color={color} />
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Fuel Requests</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#ea580c' }}>{d.fuelTotal}</span>
                          </div>
                          <ProgressBar value={d.fuelTotal} max={Math.max(...depts.map(x=>x.fuelTotal),1)} color="#ea580c" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="card fade d4" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart2 size={15} color="#2563eb" />
                <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>All Departments</span>
              </div>
              {depts.length === 0
                ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No department data</div>
                : <table className="tbl">
                    <thead><tr><th>#</th><th>Department</th><th>MR Total</th><th>MR Issued</th><th>Fulfillment</th><th>Fuel Reqs</th><th>Liters Req.</th><th>Volume</th></tr></thead>
                    <tbody>
                      {depts.map((d, i) => {
                        const color = getDC(d.dept);
                        const fRate = d.mrTotal > 0 ? Math.round((d.mrIssued/d.mrTotal)*100) : 0;
                        const rc = fRate >= 70 ? '#16a34a' : fRate >= 40 ? '#d97706' : '#dc2626';
                        return (
                          <tr key={d.dept} className={`fade d${Math.min(i+1,8)}`} style={{ animationDelay: `${i*.04}s` }}>
                            <td><span style={{ fontSize: 11, fontWeight: 700, color: '#cbd5e1' }}>{i+1}</span></td>
                            <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                              <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{d.dept}</span>
                            </div></td>
                            <td><span style={{ fontSize: 15, fontWeight: 800, color }}>{d.mrTotal}</span></td>
                            <td><span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{d.mrIssued}</span></td>
                            <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: rc, minWidth: 32 }}>{fRate}%</span>
                              <ProgressBar value={d.mrIssued} max={d.mrTotal||1} color={rc} />
                            </div></td>
                            <td><span style={{ fontSize: 13, fontWeight: 700, color: '#ea580c' }}>{d.fuelTotal}</span></td>
                            <td><span style={{ fontSize: 13, fontWeight: 600, color: '#0891b2' }}>{d.fuelLiters.toFixed(0)}L</span></td>
                            <td style={{ width: 110 }}><ProgressBar value={d.mrTotal} max={maxDeptMR} color={color} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              }
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
}